import { CircleUserRound, MessageSquare, SlidersHorizontal, Users } from "lucide-react";
import { useCallback, useRef, useState, type ReactNode, type Ref, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router";
import { useIch } from "../api/abfragen";
import { PersonAnsichtBlatt } from "../seiten/PersonAnsichtBlatt";
import { RueckmeldungBlatt } from "../seiten/RueckmeldungBlatt";
import { RM } from "../seiten/rueckmeldung/satzquelle";
import { Chip } from "./Badge";
import { initialen, planName } from "./initialen";
import { Menue, MenueEintrag, MenueKopf } from "./Menue";

/** Profil statt Zahnrad (W22d-w4, D-067, wie die iOS-App ab W22d-8, `Profil.swift`): oben links ein
 *  runder Kreis mit den Initialen. Am Desktop als Kopf der Seitenleiste (mit Name, Haushalt und Plan), in der Rail nur der
 *  Kreis, mobil oben links auf den Seiten der Tab-Leiste. Kennung `profil` (wie `accessibilityIdentifier("profil")`).
 *  W82 Web-5f (D-562, H1, BS10): der Kreis öffnet auf jeder Breite das Menü mit „Einstellungen“ und
 *  „Personen-Ansicht ändern“ (`ProfilMenue`), Name „Profil, Menü“ (iOS sagt „Profil“, das System spricht den Zusatz). */
export const PROFIL_NAME = "Profil, Menü";

const GROESSE = { mittel: "size-(--marke) t-footnote font-semibold", gross: "size-(--profil-gross) t-title-2" };

/** Der Kreis: Initialen in der Farbe der ersten Person; ohne Namen das Personen-Symbol. Stumm, den
 *  Namen traegt der Link drumherum. */
export function ProfilZeichen(props: { name: string | null | undefined; groesse?: keyof typeof GROESSE }) {
  const zeichen = initialen(props.name);
  const groesse = props.groesse ?? "mittel";
  if (!zeichen) return <CircleUserRound size={groesse === "gross" ? 56 : 32} strokeWidth={1.25} aria-hidden className="shrink-0 text-pg-accent-text" />;
  return (
    <span aria-hidden className={`inline-grid shrink-0 place-items-center rounded-pill bg-pg-person-1-soft text-pg-person-1 ${GROESSE[groesse]}`}>
      {zeichen}
    </span>
  );
}

type Form = "knopf" | "rail" | "kopfzeile";

/** W82 Web-5f: das Kürzel als Auslöser des Menüs (`aria-haspopup="menu"`, `aria-expanded` von Radix), 44 x 44 oder höher; es
 *  trägt `aria-current="page"` und die aktive Gestalt, solange die Einstellungen offen sind. „Einstellungen“ führt hin,
 *  „Personen-Ansicht ändern“ öffnet das Fenster. Escape und Tipp daneben geben den Fokus an das Kürzel zurück (Radix);
 *  nach „Personen-Ansicht ändern“ nimmt ihn erst das Kürzel, dann öffnet das Fenster, das sich das Kürzel als Ziel merkt.
 *  5g fliegt in der Tour hierher und öffnet das Menü über diesen Baustein.
 *  W82 Beta-W (D-637, F4 = A): dritter Eintrag „Rückmeldung geben“ für alle Konten, nach dem Muster von „Personen-Ansicht
 *  ändern“ (erst Fokus auf das Kürzel, dann das Blatt, das ihn dorthin zurückgibt). W82 Beta-W2 (D-701, HB2): das Zeichen „Beta“
 *  steht nicht mehr neben dem Profil, sondern unten in Seitenleiste und Rail (`BetaLeiste`); am Handy (nur `knopf`) als Kopf des
 *  Menüs ohne Handlung, nie in der Kopfzeile. Ohne `beta` bleibt das DOM wie vor Beta-W. */
export function ProfilMenue(props: { form: Form }) {
  const ort = useLocation();
  const navigieren = useNavigate();
  const { data: ich } = useIch();
  const beta = ich?.beta === true;
  const aktiv = ort.pathname === "/einstellungen" || ort.pathname.startsWith("/einstellungen/");
  const knopf = useRef<HTMLButtonElement>(null);
  const gewaehlt = useRef<"personen" | "rueckmeldung" | null>(null);
  const [fenster, setFenster] = useState(false);
  const [rueckmeldung, setRueckmeldung] = useState(false);
  return (
    <>
      <Menue
        ausloeser={<ProfilAusloeser form={props.form} aktiv={aktiv} knopfRef={knopf} />}
        ausrichtung="start"
        kennung="profil-menue"
        klasse="profil-menue"
        onCloseAutoFocus={(e) => {
          const wahl = gewaehlt.current;
          if (!wahl) return;
          gewaehlt.current = null;
          e.preventDefault();
          knopf.current?.focus({ preventScroll: true });
          if (wahl === "personen") setFenster(true);
          else setRueckmeldung(true);
        }}
      >
        {beta && props.form === "knopf" && (
          <MenueKopf>
            <span className="beta-zeichen-kopf" data-testid="beta-zeichen">
              <Chip zustand="akzent" icon={null}>
                {RM["beta.zeichen"]}
              </Chip>
            </span>
          </MenueKopf>
        )}
        <MenueEintrag icon={SlidersHorizontal} onSelect={() => navigieren("/einstellungen")}>
          Einstellungen
        </MenueEintrag>
        <MenueEintrag icon={Users} onSelect={() => (gewaehlt.current = "personen")}>
          Personen-Ansicht ändern
        </MenueEintrag>
        <MenueEintrag icon={MessageSquare} onSelect={() => (gewaehlt.current = "rueckmeldung")}>
          {RM["rueckmeldung.menue"]}
        </MenueEintrag>
      </Menue>
      <PersonAnsichtBlatt offen={fenster} onSchliessen={() => setFenster(false)} fokusNachher={() => knopf.current} />
      <RueckmeldungBlatt
        offen={rueckmeldung}
        modus="rueckmeldung"
        onSchliessen={() => setRueckmeldung(false)}
        fokusNachher={() => knopf.current}
      />
    </>
  );
}

/** Auslöser je Ort: mobil der Kreis (nur unter 744 px), in der Rail der Kreis in einer Zeile 48, in der Seitenleiste Kreis,
 *  Name, Haushalt und Plan. Radix (`Trigger asChild`) reicht seine Merkmale an den Knopf weiter. */
function ProfilAusloeser(props: { form: Form; aktiv: boolean; knopfRef: RefObject<HTMLButtonElement | null>; ref?: Ref<HTMLButtonElement> } & Record<string, unknown>) {
  const { form, aktiv, knopfRef, ref: radixRef, ...radix } = props;
  const { data: ich } = useIch();
  // React 19 reicht den Ref von Radix als Merkmal herein: beide bekommen den Knopf (Radix misst daran die Lage des Menüs)
  const ref = useCallback(
    (el: HTMLButtonElement | null) => {
      knopfRef.current = el;
      if (typeof radixRef === "function") radixRef(el);
      else if (radixRef) radixRef.current = el;
    },
    [knopfRef, radixRef],
  );
  const gestalt = aktiv ? "bg-pg-surface shadow-karte" : "hover:bg-pg-surface-2";
  const gemeinsam = { ...radix, ref, type: "button" as const, "data-testid": "profil", "aria-current": aktiv ? ("page" as const) : undefined };
  if (form === "knopf") {
    return (
      <button {...gemeinsam} aria-label={PROFIL_NAME} className="-ml-1.5 grid size-(--touch) place-items-center rounded-pill md:hidden">
        {/* W82-2 N1 (S2): auch auf „Briefe“; deren Unit-Tests antworten auf `ich` ohne Nutzer, dann das Personen-Symbol */}
        <ProfilZeichen name={ich?.nutzer?.name} />
      </button>
    );
  }
  if (form === "rail") {
    return (
      <button
        {...gemeinsam}
        aria-label={PROFIL_NAME}
        title={PROFIL_NAME}
        className={`mb-3 flex min-h-(--pg-hit) w-full shrink-0 items-center justify-center rounded-md transition-colors duration-(--pg-dur-fast) ${gestalt}`}
      >
        <ProfilZeichen name={ich?.nutzer.name} />
      </button>
    );
  }
  const name = ich?.nutzer.name || ich?.nutzer.email || "";
  return (
    <button
      {...gemeinsam}
      className={`mb-3 w-full shrink-0 flex min-h-(--sidebar-row) items-center gap-3 rounded-md px-2 py-2 text-left transition-colors duration-(--pg-dur-fast) ${gestalt}`}
    >
      <ProfilZeichen name={ich?.nutzer.name} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="sr-only">{PROFIL_NAME}: </span>
        <span className="t-footnote truncate font-semibold text-pg-text">{name}</span>
        {ich && (
          <span className="t-caption-1 truncate text-pg-text-2">
            {ich.haushalt.name} · Plan {planName(ich.haushalt.plan)}
          </span>
        )}
      </span>
    </button>
  );
}

/** Mobil oben links (Platz `links` der Seite, nur unter 744 px, wo die Tab-Leiste steht). */
export function ProfilKnopf(): ReactNode {
  return <ProfilMenue form="knopf" />;
}

/** Kopf der Rail: nur der Kreis, mindestens 48 hoch wie die Eintraege. */
export function ProfilRail(): ReactNode {
  return <ProfilMenue form="rail" />;
}

/** Kopf der Seitenleiste: Kreis, Name und darunter Haushalt und Plan. Der zugaengliche Name beginnt mit „Profil, Menü“ und
 *  enthaelt den sichtbaren Text (Label in Name). */
export function ProfilKopfzeile(): ReactNode {
  return <ProfilMenue form="kopfzeile" />;
}
